import { useLocation } from "react-router";
import { useRecordsViewOptions } from "@/state/recordsView";

/**
 * Which parts of the app frame a route actually wants.
 *
 * Both sidebars and the header's interval are answers to "when is this?", and
 * two places have no such question. A single record's page is one: it shows
 * one record, reached by name, and neither the time hierarchy nor the
 * collection list has anything to do while it is on screen. A collection's
 * notes tab is the other: note records carry no interval at all, so a time
 * cursor over them moves nothing.
 *
 * Decided in one place because `App` (which mounts the panels) and `Header`
 * (which mounts the controls that open them) sit on opposite sides of the
 * `Outlet` and must not disagree — a header button that toggles a panel which
 * is not rendered does nothing at all.
 *
 * Read off the pathname rather than `useParams`: `App` is the layout route's
 * element, so its RouteContext stops at "/" and the child's `:userID` and
 * `:collectionID` never reach its params.
 */

/** First segments that are routes in their own right, not a `:userID`. */
const RESERVED = new Set(["admin", "login", "record"]);

function isRecordPath(parts: string[]) {
  // The legacy permalink, and the readable form with its reserved "-" segment.
  return (
    (parts[0] === "record" && parts.length === 2) ||
    (parts.length === 3 && parts[1] === "-")
  );
}

function isCollectionPath(parts: string[]) {
  return parts.length === 2 && !RESERVED.has(parts[0]);
}

export interface AppChrome {
  /** A single record's page, under either of its two URLs. */
  isRecordPage: boolean;
  /** `:userID` from the readable record URL; absent on the legacy permalink. */
  recordUserRef?: string;
  /** `:recordID` from the legacy permalink; absent on the readable URL. */
  recordID?: string;
  /** Mount the time sidebar, and with it the ⌘B shortcut and the swipe. */
  showTimeSidebar: boolean;
  /** Mount the collections sidebar, and with it the ⌘J shortcut. */
  showCollectionsSidebar: boolean;
  /** Name the current interval in the header. */
  showInterval: boolean;
}

export function useAppChrome(): AppChrome {
  const { pathname } = useLocation();
  const { options } = useRecordsViewOptions();

  const parts = pathname.split("/").filter(Boolean);
  const isRecordPage = isRecordPath(parts);
  // The same fallback `Records` applies: a stored "notes" mode means nothing
  // outside a collection, and there the timeline is what is on screen.
  const notesMode = options.mode === "notes" && isCollectionPath(parts);
  // Routes that are neither a list nor a record, so there is no interval to
  // name — but the panels stay, as the way back out of them.
  const bare = parts[0] === "admin" || parts[0] === "login";

  return {
    isRecordPage,
    recordUserRef: isRecordPage && parts[1] === "-" ? parts[0] : undefined,
    recordID: isRecordPage && parts[0] === "record" ? parts[1] : undefined,
    showTimeSidebar: !isRecordPage && !notesMode,
    showCollectionsSidebar: !isRecordPage,
    showInterval: !isRecordPage && !notesMode && !bare,
  };
}
